import { Header } from "@/components/conversation-details/header.tsx";
import {
  MentorshipDetails,
  useGetConversationSummary,
  useGetMentorshipFeedback,
  useGetSuggestedTopics,
} from "@/lib/api/mentorships.ts";
import { useNotes } from "@/lib/api/notes.ts";
import { useSessions } from "@/lib/api/sessions";
import {
  useGetUserSupervisorAndChannel,
  useIsRematched,
  User,
  useUser,
} from "@/lib/api/user.ts";
import { useGetChannelMentorship } from "@/lib/hooks/conversations/use-get-channel-mentorship.ts";
import { useChannelContext } from "@sendbird/uikit-react/Channel/context";
import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import {
  CalendarIcon,
  CheckCheckIcon,
  FileTextIcon,
  Loader2,
  SearchIcon,
  UserIcon,
} from "lucide-react";
import { Escalation } from "../conversations/escalation/index.tsx";
import { DetailedUserInfo } from "./side-bar/detailed-user-info.tsx";
import { Tab } from "./side-bar/tab-switch.tsx";
import { NoMentorship } from "../shared/no-mentorship.tsx";

const TABS = [
  {
    icon: <FileTextIcon className="size-4" />,
    id: "notes",
    label: "Documentation",
  },
  {
    icon: <CalendarIcon className="size-4" />,
    id: "calendar",
    label: "Calendar",
  },
  {
    icon: <UserIcon className="size-4" />,
    id: "contact",
    label: "Contact",
  },
  {
    icon: <CheckCheckIcon className="size-4" />,
    id: "feedback",
    label: "Feedback",
  },
  {
    icon: <SearchIcon className="size-4" />,
    id: "search",
    label: "Search Messages",
  },
];

export type SurveyThresholdLabels = {
  high: string;
  moderate: string;
  low: string;
};

export type SurveyThresholdConfig = {
  high: number;
  moderate: number;
  inverse: boolean;
  labels: SurveyThresholdLabels;
};

export type SurveyThresholds = {
  phq: SurveyThresholdConfig;
  gad: SurveyThresholdConfig;
  who: SurveyThresholdConfig;
};

const SURVEY_THRESHOLDS: SurveyThresholds = {
  phq: {
    high: 20,
    moderate: 10,
    inverse: false,
    labels: {
      high: "High",
      moderate: "Moderate",
      low: "Low",
    },
  },
  gad: {
    high: 15,
    moderate: 10,
    inverse: false,
    labels: {
      high: "High",
      moderate: "Moderate",
      low: "Low",
    },
  },
  who: {
    high: 36,
    moderate: 52,
    labels: {
      high: "High",
      moderate: "Moderate",
      low: "Low",
    },
    inverse: true, // Higher scores are better for WHO-5
  },
} as const;

interface ConversationDetailsInnerProps {
  menteeId: string;
  mentorId: string;
  mentorship: MentorshipDetails;
  adminMode?: boolean;
  isMentorshipsLoading?: boolean;
  isCollapsed?: boolean;
}

export function ConversationDetailsInner({
  menteeId,
  mentorId,
  adminMode,
  mentorship,
}: ConversationDetailsInnerProps) {
  const {
    data: userSupervisorAndChannel,
    isLoading: isSupervisorAndChannelLoading,
  } = useGetUserSupervisorAndChannel(mentorId, !!mentorId);
  const { data: notes, isLoading, isError } = useNotes(mentorship?.id);
  const { data: sessions, isLoading: isSessionLoading } = useSessions(menteeId);
  const { data: feedback } = useGetMentorshipFeedback(
    adminMode ? mentorship?.id : undefined,
  );
  const { data: isRematched } = useIsRematched(menteeId);
  const { data: mentee } = useUser(menteeId);
  const { data: suggestedTopics } = useGetSuggestedTopics(mentorship?.id);
  const { data: summary } = useGetConversationSummary(mentorship?.id);

  const monthlyUpdateNotes =
    notes?.filter((note) => note?.metadata?.type === "mentor_monthly_update") ||
    [];

  const latestMonthlyUpdate =
    monthlyUpdateNotes.length > 0
      ? monthlyUpdateNotes.reduce((latest, current) => {
          return new Date(current.createdAt) > new Date(latest.createdAt)
            ? current
            : latest;
        })
      : null;

  if (latestMonthlyUpdate) {
    latestMonthlyUpdate.metadata = {
      ...latestMonthlyUpdate.metadata,
      latestUpdate: true,
    };
  }

  const filteredTabs = TABS.filter(
    (tab) =>
      !(tab.id === "feedback" && !adminMode) &&
      !(tab.id === "search" && !adminMode),
  );

  if (isSupervisorAndChannelLoading || isLoading || isSessionLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-red-500">Error fetching data</div>
      </div>
    );
  }

  if (!mentorship && !isLoading) {
    return <NoMentorship />;
  }

  if (!mentee && !isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-red-500">Error fetching mentee</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-gray-50 p-4">
      <DetailedUserInfo
        mentee={mentee as User}
        mentorship={mentorship}
        feedback={feedback || null}
        isRematched={isRematched || false}
        surveyThresholds={SURVEY_THRESHOLDS}
        suggestedTopics={suggestedTopics?.data?.topics || []}
        summary={summary?.data?.summary || ""}
        filteredTabs={filteredTabs as unknown as Tab[]}
        notes={notes || []}
        sessions={sessions || []}
        adminMode={adminMode || false}
        supervisor={userSupervisorAndChannel?.supervisor || null}
      />
      <Escalation mentorshipId={mentorship?.id as string} />
    </div>
  );
}

interface ConversationDetailsProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
}

export function ConversationDetails({
  isCollapsed,
  onToggleCollapse,
}: ConversationDetailsProps) {
  const store = useSendbirdStateContext();
  const channelStore = useChannelContext();
  const channel = channelStore?.currentGroupChannel;

  const membersExcludingMe = channel?.members?.filter(
    (member) => member.userId !== store.config.userId,
  );

  const { mentorship, isLoading } = useGetChannelMentorship();

  if (membersExcludingMe?.length === 1) {
    return (
      <div className="flex h-full flex-col">
        <Header isCollapsed={isCollapsed} onToggleCollapse={onToggleCollapse} />

        <ConversationDetailsInner
          menteeId={membersExcludingMe?.[0].userId}
          mentorId={store.config.userId}
          mentorship={mentorship!}
          isMentorshipsLoading={isLoading}
          isCollapsed={isCollapsed}
        />
      </div>
    );
  }

  // @todo - handle multiple members
  return null;
}
